import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  FaCloud,
  FaShieldAlt,
  FaCogs,
  FaBolt,
  FaNetworkWired,
  FaServer,
  FaDocker,
  FaEye,
} from 'react-icons/fa';
import { ink, sky, paper, paperMuted, borderInk, borderInkHover, bodyMuted } from '../theme';
import BlueprintSectionTitle from './BlueprintSectionTitle';

const FeaturesSection = styled.section`
  padding: 100px 0;
  background: ${paperMuted};
  position: relative;
  font-family: 'Inter', system-ui, sans-serif;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const SectionSubtitle = styled.p`
  font-size: 1.05rem;
  text-align: center;
  margin-bottom: 3rem;
  color: ${bodyMuted};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(350px, 1fr));
  gap: 30px;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 25px;
    margin-bottom: 3rem;
  }

  @media (max-width: 480px) {
    gap: 20px;
    margin-bottom: 2.5rem;
  }

  @media (max-width: 360px) {
    gap: 15px;
    margin-bottom: 2rem;
  }
`;

const FeatureCard = styled(motion.div)`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  overflow: hidden;
  text-align: left;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.05);

  &:hover {
    border-color: ${borderInkHover};
    box-shadow: 0 10px 28px rgba(3, 26, 56, 0.08);
    transform: translateY(-3px);
  }

  @media (max-width: 768px) {
    border-radius: 2px;
  }
`;

const FeatureCardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px 18px calc(20px + 5px);
  position: relative;
  overflow: hidden;
  background: linear-gradient(168deg, #0e3a63 0%, ${ink} 52%, #021427 100%);
  border-bottom: 1px solid rgba(142, 207, 255, 0.22);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.12;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px);
    background-size: 12px 12px;
    pointer-events: none;
    filter: blur(0.2px);
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: linear-gradient(180deg, ${sky} 0%, rgba(142, 207, 255, 0.35) 100%);
    box-shadow: 3px 0 12px rgba(142, 207, 255, 0.18);
    pointer-events: none;
  }
`;

const FeatureIcon = styled.div`
  position: relative;
  z-index: 1;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: ${sky};

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 1.15rem;
  }
`;

const FeatureTitle = styled.h3`
  position: relative;
  z-index: 1;
  margin: 0;
  padding-left: 0.15rem;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #f8fbff;
  line-height: 1.35;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);

  @media (max-width: 480px) {
    font-size: 0.72rem;
    letter-spacing: 0.1em;
  }
`;

const FeatureCardBody = styled.div`
  padding: 1.1rem 1.25rem 1.2rem;
`;

const FeatureDescription = styled.p`
  color: ${bodyMuted};
  line-height: 1.6;
  font-size: 0.95rem;
  margin: 0 0 1rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.85rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const FeatureDetails = styled.div`
  background: ${paperMuted};
  border: 1px solid ${borderInk};
  border-left: 3px solid ${sky};
  padding: 14px 16px;

  @media (max-width: 480px) {
    padding: 12px 14px;
  }
`;

const FeatureDetailsTitle = styled.h4`
  color: ${ink};
  font-size: 0.72rem;
  margin: 0 0 8px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
`;

const FeatureDetailsText = styled.p`
  color: ${bodyMuted};
  font-size: 0.85rem;
  line-height: 1.55;
  margin: 0;
`;

const HighlightSection = styled.div`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  overflow: hidden;
  text-align: left;
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.05);
`;

const HighlightHead = styled.div`
  position: relative;
  padding: 16px 22px 18px calc(22px + 5px);
  overflow: hidden;
  background: linear-gradient(168deg, #0e3a63 0%, ${ink} 52%, #021427 100%);
  border-bottom: 1px solid rgba(142, 207, 255, 0.22);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.12;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px);
    background-size: 12px 12px;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: linear-gradient(180deg, ${sky} 0%, rgba(142, 207, 255, 0.35) 100%);
    pointer-events: none;
  }
`;

const HighlightTitle = styled.h3`
  position: relative;
  z-index: 1;
  margin: 0;
  padding-left: 0.35rem;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #f8fbff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
`;

const HighlightBody = styled.div`
  padding: 1.25rem 1.35rem 1.35rem;
`;

const HighlightText = styled.p`
  color: ${bodyMuted};
  font-size: 1rem;
  line-height: 1.65;
  max-width: 800px;
  margin: 0;
`;

const Features = () => {
  const features = [
    {
      icon: <FaCloud />,
      title: "Интеграция и совместимость",
      description: "Поддержка широкого спектра платформ виртуализации и контейнеризации для централизованного управления безопасностью",
      details: "VMware, Proxmox, zVirt, Базис, Docker, Kubernetes и другие платформы. Интеграция с SIEM-системами для углубленного анализа угроз"
    },
    {
      icon: <FaCogs />,
      title: "Управление и автоматизация",
      description: "Распределенная архитектура с автоматической генерацией правил безопасности на основе политик и связей",
      details: "Установка агентов на серверах и ВМ, управление группами объектов, создание гибких шаблонов политик безопасности"
    },
    {
      icon: <FaShieldAlt />,
      title: "Безопасность и сегментация",
      description: "Сегментация трафика в пределах одного VLAN, изоляция приложений и поддержка модели безопасности Zero Trust",
      details: "Сегментация приложений, поддержка микросервисной архитектуры, контроль скрытых сервисов и детальный контроль взаимодействий"
    },
    {
      icon: <FaEye />,
      title: "Мониторинг и аналитика",
      description: "Сбор данных о трафике, аномалиях и событиях в реальном времени с визуализацией топологии сети",
      details: "Мониторинг трафика и событий, интерактивная карта сети с фильтрами, аудит и автоматические отчеты"
    },
    {
      icon: <FaBolt />,
      title: "Оптимизация производительности и контроль состояния",
      description: "Снижение нагрузки на периметральные средств защиты, управление изменениями, повышений устойчивости функционирования инфраструктуры",
      details: "Контроль безопасности ВМ при миграции, создание дополнительного эшелона безопасности с возможностью снижения требований к производительности и функциональным возможностям периметральных средств защиты – оптимизация стоимости создания и владения, повышение общей надежности инфраструктуры"
    },
    {
      icon: <FaNetworkWired />,
      title: "Распределенная архитектура",
      description: "Масштабируемость системы за счет распределения нагрузки между узлами и автономная работа агентов",
      details: "Распределение функций управления и контроля между узлами, автономная работа агентов при сбоях сети"
    },
    {
      icon: <FaServer />,
      title: "Автоматическое обнаружение",
      description: "Идентификация устройств и их типов в сети с автоматической классификацией для упрощения управления",
      details: "Автоматическое обнаружение новых устройств, классификация по типам, упрощение управления инфраструктурой"
    },
    {
      icon: <FaDocker />,
      title: "DevOps / DevSecOps интеграция",
      description: "Открытый API для интеграции с DevOps-инструментами и автоматизации процессов CI/CD",
      details: "API для интеграции с внешними системами, автоматизация процессов безопасности в DevOps-практиках"
    }
  ];

  return (
    <FeaturesSection id="features">
      <Container>
        <BlueprintSectionTitle>Ключевые функции</BlueprintSectionTitle>
        <SectionSubtitle>
          e-node оазис (cloud) предоставляет широкий набор функциональных возможностей для решения типовых проблем эксплуатации распределенной гибридной ИТ-инфраструктуры
        </SectionSubtitle>

        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <FeatureCardHead>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
              </FeatureCardHead>
              <FeatureCardBody>
                <FeatureDescription>{feature.description}</FeatureDescription>
                <FeatureDetails>
                  <FeatureDetailsTitle>Технические детали</FeatureDetailsTitle>
                  <FeatureDetailsText>{feature.details}</FeatureDetailsText>
                </FeatureDetails>
              </FeatureCardBody>
            </FeatureCard>
          ))}
        </FeaturesGrid>

        <HighlightSection>
          <HighlightHead>
            <HighlightTitle>Ключевые преимущества e-node оазис (cloud)</HighlightTitle>
          </HighlightHead>
          <HighlightBody>
            <HighlightText>
              Система обеспечивает централизованное управление средствами безопасности, автоматизацию
              рутинных задач, повышение прозрачности и снижение рисков компрометации. Распределенная
              архитектура гарантирует масштабируемость без потери производительности, а интеграция
              с современными платформами упрощает внедрение в существующую инфраструктуру.
            </HighlightText>
          </HighlightBody>
        </HighlightSection>
      </Container>
    </FeaturesSection>
  );
};

export default Features;
